import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Heart, MessageCircle, Share2, Bookmark } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import InFeedAd from '@/components/InFeedAd';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Feed = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  const isArtist = user?.user_type === 'independent_artist' ||
    user?.user_type === 'studio_artist' ||
    user?.user_type === 'studio' ||
    user?.user_type === 'artist';

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const response = await axios.get(`${API}/artworks`);
      setArtworks(response.data);
    } catch (error) {
      console.error('Error fetching feed:', error);
      toast.error('Error al cargar el feed');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (artworkId) => {
    try {
      const response = await axios.post(`${API}/artworks/${artworkId}/like`);
      setArtworks(prev => prev.map(art =>
        art.id === artworkId
          ? { ...art, is_liked: response.data.liked, likes_count: response.data.likes_count }
          : art
      ));
    } catch (error) {
      console.error('Error liking artwork:', error);
    }
  };

  const handleShare = async (artwork) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/artwork/${artwork.id}`);
      toast.success('Enlace copiado al portapapeles');
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="aspect-[4/5] bg-zinc-900 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (artworks.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="font-['Cinzel'] text-3xl font-bold text-white mb-4">
          {t('feed.welcome')}
        </h2>
        <p className="text-zinc-500 mb-8">
          {isArtist
            ? t('feed.artistEmpty')
            : t('feed.clientEmpty')}
        </p>
        {isArtist ? (
          <Link
            to="/upload"
            className="inline-block bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold px-8 py-4"
            data-testid="upload-first-btn"
          >
            {t('feed.uploadArt')}
          </Link>
        ) : (
          <Link
            to="/search"
            className="inline-block bg-zinc-100 text-zinc-950 hover:bg-white uppercase tracking-widest text-xs font-bold px-8 py-4"
            data-testid="search-artists-btn"
          >
            {t('feed.searchArtists')}
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-0 md:px-4 py-2 sm:py-4 md:py-8" data-testid="feed-container">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
        {artworks.map((artwork, index) => (
          <>
            {index > 0 && index % 6 === 0 && (
              <div key={`ad-${index}`} className="col-span-1 md:col-span-2 lg:col-span-3">
                <InFeedAd slot="3497156484" />
              </div>
            )}
            <article
              key={artwork.id}
              className="relative group overflow-hidden aspect-[4/5] bg-zinc-900"
              data-testid={`artwork-${artwork.id}`}
            >
              <img
                src={artwork.image_url}
                alt={artwork.title}
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                loading="lazy"
              />

              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  {/* Artist Info */}
                  <Link
                    to={`/profile/${artwork.artist_id}`}
                    className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
                  >
                    <Avatar className="h-10 w-10 border border-zinc-700">
                      <AvatarImage src={artwork.artist_avatar} />
                      <AvatarFallback className="bg-zinc-800 text-zinc-400 text-sm">
                        {artwork.artist_name?.charAt(0)?.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-white font-medium">{artwork.artist_name}</p>
                      <p className="text-xs text-zinc-500">
                        {formatDistanceToNow(new Date(artwork.created_at), { addSuffix: true, locale: es })}
                      </p>
                    </div>
                  </Link>

                  {/* Title & Style */}
                  <h3 className="font-['Cinzel'] text-lg font-semibold text-white mb-1">{artwork.title}</h3>
                  {artwork.style && (
                    <span className="inline-block text-xs uppercase tracking-wider text-zinc-500">
                      {artwork.style}
                    </span>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-4 mt-4 pt-4 border-t border-zinc-800/50">
                    <button
                      onClick={() => handleLike(artwork.id)}
                      className={`flex items-center gap-2 transition-colors ${artwork.is_liked ? 'text-rose-500' : 'text-zinc-400 hover:text-white'
                        }`}
                      data-testid={`like-btn-${artwork.id}`}
                    >
                      <Heart size={20} fill={artwork.is_liked ? 'currentColor' : 'none'} />
                      <span className="text-sm font-mono">{artwork.likes_count}</span>
                    </button>
                    <Link
                      to={`/profile/${artwork.artist_id}`}
                      className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors"
                    >
                      <MessageCircle size={20} />
                      <span className="text-sm font-mono">{artwork.comments_count}</span>
                    </Link>
                    <button
                      onClick={() => handleShare(artwork)}
                      className="text-zinc-400 hover:text-white transition-colors ml-auto"
                      data-testid={`share-btn-${artwork.id}`}
                    >
                      <Share2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </>
        ))}
      </div>
    </div>
  );
};

export default Feed;
